import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiService } from '../../demands/services/api'
import { toast } from '../../../components/Toast'
import type { Setor } from '../../../models'

// Tipos para os payloads
type CreateSetorPayload = {
  nome_setor: string
  chefe_setor_fk?: number
  cidade_fk: number
  equipe?: boolean
  usuarios?: number[]
}

type UpdateSetorPayload = {
  nome_setor: string
  chefe_setor_fk?: number
  equipe?: boolean
}

// Filtros para setores
export type SetoresFilters = {
  buscar?: string
  equipe?: boolean
}

// Query keys
export const setoresKeys = {
  all: ['setores'] as const,
  lists: () => [...setoresKeys.all, 'list'] as const,
  list: (filters?: SetoresFilters) => [...setoresKeys.lists(), filters] as const,
  details: () => [...setoresKeys.all, 'detail'] as const,
  detail: (id: number) => [...setoresKeys.details(), id] as const,
}

// Opções adicionais para o hook
type UseSetoresOptions = {
  enabled?: boolean
}

// Hook para listar setores com filtros
export const useSetores = (filters?: SetoresFilters, options?: UseSetoresOptions) => {
  return useQuery({
    queryKey: setoresKeys.list(filters),
    queryFn: async () => {
      return await apiService.getSetoresCompletos(filters ?? {})
    },
    staleTime: 1000 * 60 * 5, // 5 minutos
    refetchOnMount: true, // Sempre refetch ao montar
    enabled: options?.enabled ?? true,
  })
}

// Hook para criar setor
export const useCreateSetor = () => {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async (data: CreateSetorPayload) => {
      return await apiService.createSetor(data)
    },
    onSuccess: () => {
      // Invalidar e refetch cache de setores para recarregar todas as listas
      // type: 'all' força refetch mesmo de queries inativas
      queryClient.invalidateQueries({ queryKey: setoresKeys.lists() })
      queryClient.refetchQueries({ queryKey: setoresKeys.lists(), type: 'all' })
      toast.success('Setor criado com sucesso!')
    },
    onError: (error) => {
      console.error('Erro ao criar setor:', error)
      toast.error('Erro ao criar setor. Tente novamente.')
    },
  })
}

// Hook para criar equipe
export const useCreateEquipe = () => {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async (data: CreateSetorPayload) => {
      return await apiService.createEquipe(data)
    },
    onSuccess: () => {
      // Invalidar e refetch cache de setores para recarregar todas as listas
      // type: 'all' força refetch mesmo de queries inativas
      queryClient.invalidateQueries({ queryKey: setoresKeys.lists() })
      queryClient.refetchQueries({ queryKey: setoresKeys.lists(), type: 'all' })
      toast.success('Equipe criada com sucesso!')
    },
    onError: (error) => {
      console.error('Erro ao criar equipe:', error)
      toast.error('Erro ao criar equipe. Tente novamente.')
    },
  })
}

// Hook para atualizar setor
export const useUpdateSetor = () => {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: UpdateSetorPayload }) => {
      return await apiService.updateSetor(id, data)
    },
    onSuccess: (_, variables) => {
      // Invalidar e refetch cache de setores para recarregar todas as listas
      // type: 'all' força refetch mesmo de queries inativas
      queryClient.invalidateQueries({ queryKey: setoresKeys.lists() })
      queryClient.refetchQueries({ queryKey: setoresKeys.lists(), type: 'all' })
      // Invalidar cache do setor específico
      queryClient.invalidateQueries({ queryKey: setoresKeys.detail(variables.id) })
      toast.success('Setor atualizado com sucesso!')
    },
    onError: (error) => {
      console.error('Erro ao atualizar setor:', error)
      toast.error('Erro ao atualizar setor. Tente novamente.')
    },
  })
}

// Hook para deletar setor
export const useDeleteSetor = () => {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async (setor: Setor) => {
      return await apiService.deleteSetor(setor.id)
    },
    onSuccess: () => {
      // Invalidar e refetch cache de setores para recarregar todas as listas
      // type: 'all' força refetch mesmo de queries inativas
      queryClient.invalidateQueries({ queryKey: setoresKeys.lists() })
      queryClient.refetchQueries({ queryKey: setoresKeys.lists(), type: 'all' })
      toast.success('Setor excluído com sucesso!')
    },
    onError: (error) => {
      console.error('Erro ao excluir setor:', error)
      toast.error('Erro ao excluir setor. Tente novamente.')
    },
  })
}
